import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { gpx as gpxToGeoJSON } from '@tmcw/togeojson';
import {
  booleanIntersects,
  length as turfLength,
  polygon as turfPolygon,
  circle as turfCircle,
  multiPolygon as turfMultiPolygon,
} from '@turf/turf';

import { db, loadAllTracks, saveTrack, removeTrack, clearAllTracks, trackHashExists, hashString } from './db.js';
import { searchPlace } from './nominatim.js';
import { BASEMAPS, BASEMAP_ORDER, DEFAULT_BASEMAP } from './basemaps.js';
import { ownerColor, uniqueOwners, IMPORTED_LABEL } from './owners.js';
import { tracksToGPX, downloadGPX, safeFilename, QUERENCIA_XMLNS } from './gpx-export.js';
import { t, getLang, setLang, applyStaticTranslations, SUPPORTED } from './i18n.js';
import { getMode, setMode, initMode, onSystemChange } from './theme.js';

// Etiqueta de propietario "Importado" traducida solo para mostrar
// (el valor canónico en datos sigue siendo IMPORTED_LABEL).
function ownerDisplay(label) {
  return label === IMPORTED_LABEL ? t('owners.imported') : label;
}

// Eventos anónimos para Simple Analytics (sin cookies ni datos personales:
// solo el nombre del evento). No-op si el script aún no cargó / está bloqueado.
function track(event) {
  try { window.sa_event?.(event); } catch {}
}

// Clave renombrada (antes 'wikitot.basemap'): resetea el mapa "pegado" de
// quien entró antes de que el default fuese satélite → todos parten del default.
const BASEMAP_STORAGE_KEY = 'querencia.basemap';

// -----------------------------------------------------------------------------
// State
// -----------------------------------------------------------------------------

const state = {
  tracks: [],          // { id, name, source, feature, distanceKm, addedAt }
  zone: null,          // Feature (Polygon | MultiPolygon)
  zoneMode: 'search',  // 'search' | 'radius' | 'draw'
  drawing: false,
  drawPoints: [],
  onlyInZone: false,
  radiusCenter: null,  // { lat, lon, label }
  radiusKm: 15,
  basemapId: localStorage.getItem(BASEMAP_STORAGE_KEY) || DEFAULT_BASEMAP,
  hiddenOwners: new Set(),
  selectedTrackId: null,
};

// -----------------------------------------------------------------------------
// Map
// -----------------------------------------------------------------------------

const map = new maplibregl.Map({
  container: 'map',
  style: {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {},
    layers: [
      { id: 'app-background', type: 'background', paint: { 'background-color': '#0a0c10' } },
    ],
  },
  center: [2.0823, 42.2811],
  zoom: 9,
  attributionControl: false,
});

map.addControl(new maplibregl.AttributionControl({ compact: true }));
map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

// Control propio: re-encuadrar el mapa sobre las rutas cargadas.
class FitTracksControl {
  onAdd() {
    this._container = document.createElement('div');
    this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'fit-tracks-btn';
    btn.textContent = '⌖';
    btn.title = t('map.fit');
    btn.addEventListener('click', () => { if (state.tracks.length) fitToTracks(); });
    this._container.appendChild(btn);
    return this._container;
  }
  onRemove() { this._container.remove(); }
}
map.addControl(new FitTracksControl(), 'top-right');

// Seguro extra al ResizeObserver de MapLibre: rotaciones / cambios de
// breakpoint donde el observer llegue tarde. map.resize() es idempotente.
window.addEventListener('resize', () => map.resize());

map.on('load', async () => {
  // Tracks layers (basemap se inserta debajo de tracks-outline en setBasemap)
  map.addSource('tracks', { type: 'geojson', data: emptyFC() });
  map.addLayer({
    id: 'tracks-outline',
    type: 'line',
    source: 'tracks',
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#000', 'line-width': 5, 'line-opacity': 0.35 },
  });
  map.addLayer({
    id: 'tracks-line',
    type: 'line',
    source: 'tracks',
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: {
      'line-color': [
        'case',
        ['boolean', ['get', 'inZone'], false], '#2bb36a',
        '#ff3d9a',
      ],
      'line-width': 3,
      'line-opacity': 0.7,
    },
  });
  // Ruta seleccionada: la misma fuente, más ancha y a plena opacidad.
  map.addLayer({
    id: 'tracks-highlight',
    type: 'line',
    source: 'tracks',
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#ff3d9a', 'line-width': 6.5, 'line-opacity': 1 },
    filter: ['==', ['get', 'trackId'], -1],
  });

  // Interacción mapa→lista: tocar una ruta la selecciona.
  map.on('click', 'tracks-line', (e) => {
    if (state.drawing) return;
    const f = e.features?.[0];
    const tr = f && state.tracks.find((x) => x.id === f.properties.trackId);
    if (tr) { selectTrack(tr, { lngLat: e.lngLat }); track('toca_ruta'); }
  });
  map.on('mouseenter', 'tracks-line', () => { if (!state.drawing) map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', 'tracks-line', () => { if (!state.drawing) map.getCanvas().style.cursor = ''; });
  map.on('click', (e) => {
    if (state.drawing || !state.selectedTrackId || !map.getLayer('tracks-line')) return;
    const hits = map.queryRenderedFeatures(e.point, { layers: ['tracks-line'] });
    if (!hits.length) clearTrackSelection();
  });

  // Basemap activo (persistido si el usuario lo eligió; si es el default de
  // carga no se persiste, para que el default siga siendo autoritario).
  setBasemap(state.basemapId, { fit: false, persist: false });
  renderBasemapChips();

  // Zone layers
  map.addSource('zone', { type: 'geojson', data: emptyFC() });
  map.addLayer({
    id: 'zone-fill',
    type: 'fill',
    source: 'zone',
    paint: { 'fill-color': '#ffc850', 'fill-opacity': 0.18 },
  });
  map.addLayer({
    id: 'zone-outline',
    type: 'line',
    source: 'zone',
    paint: { 'line-color': '#ffc850', 'line-width': 2 },
  });

  // Draw preview
  map.addSource('draw-preview', { type: 'geojson', data: emptyFC() });
  map.addLayer({
    id: 'draw-preview-line',
    type: 'line',
    source: 'draw-preview',
    paint: { 'line-color': '#ffc850', 'line-width': 2, 'line-dasharray': [2, 2] },
  });
  map.addLayer({
    id: 'draw-preview-points',
    type: 'circle',
    source: 'draw-preview',
    filter: ['==', '$type', 'Point'],
    paint: {
      'circle-color': '#ffc850',
      'circle-radius': 4,
      'circle-stroke-color': '#1c1f24',
      'circle-stroke-width': 2,
    },
  });

  // Load persisted tracks
  await hydrateFromDB();
});

function emptyFC() {
  return { type: 'FeatureCollection', features: [] };
}

// -----------------------------------------------------------------------------
// Persistence
// -----------------------------------------------------------------------------

async function hydrateFromDB() {
  try {
    const rows = await loadAllTracks();
    state.tracks = rows.map(rowToTrack);
    renderTracks();
    renderTrackList();
    renderOwners();
    renderStats();
    if (state.tracks.length) fitToTracks();
  } catch (err) {
    console.error('No se pudo cargar de IndexedDB:', err);
  }
}

function rowToTrack(row) {
  return {
    id: row.id,
    name: row.name,
    source: row.source,
    ownerLabel: row.ownerLabel || IMPORTED_LABEL,
    feature: row.feature,
    distanceKm: row.distanceKm,
    addedAt: row.addedAt,
    hash: row.hash,
  };
}

// -----------------------------------------------------------------------------
// GPX ingestion
// -----------------------------------------------------------------------------

const dropzone = document.getElementById('dropzone');
const fileInput = document.getElementById('file-input');

dropzone.addEventListener('click', () => fileInput.click());
fileInput.addEventListener('change', (e) => handleFiles(e.target.files));
dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('dragover'); });
dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
dropzone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropzone.classList.remove('dragover');
  handleFiles(e.dataTransfer.files);
});

// Lee los metadatos del track de tracks de Querencia: un bloque <extensions>
// a nivel de <trk> con una entrada <querencia:trk> por ruta original (en
// orden; `segs` indica cuántos <trkseg> aporta cada una). Devuelve un array
// por <trk> del documento, o null si el archivo no trae metadatos propios.
function readQuerenciaTrackMeta(xml) {
  const trks = [...xml.getElementsByTagName('trk')];
  if (!trks.length) return null;
  let any = false;
  const perTrk = trks.map((trk) => {
    const ext = [...trk.children].find((c) => c.localName === 'extensions');
    if (!ext) return null;
    const entries = [...new Set([
      ...ext.getElementsByTagNameNS(QUERENCIA_XMLNS, 'trk'),
      ...ext.getElementsByTagName('querencia:trk'),
    ])];
    if (!entries.length) return null;
    any = true;
    return entries.map((e) => {
      const pick = (tag) =>
        e.getElementsByTagNameNS(QUERENCIA_XMLNS, tag)[0]?.textContent ??
        e.getElementsByTagName(`querencia:${tag}`)[0]?.textContent ?? null;
      return {
        name: pick('name'),
        owner: pick('owner'),
        segs: Math.max(1, Number(pick('segs')) || 1),
      };
    });
  });
  return any ? perTrk : null;
}

// Convierte las features de track de un GPX en items importables.
// - GPX normal: un item por <trk>; las grabaciones con pausas (varios
//   <trkseg>) se conservan como UNA ruta (MultiLineString).
// - Track de tracks de Querencia: reconstruye las rutas originales
//   repartiendo los tramos según los metadatos embebidos (nombre,
//   propietario y nº de tramos por ruta).
// Nota: la alineación feature↔trk asume archivos sin <rte> mezclados con
// metadatos de Querencia (nuestros exports solo emiten <trk>).
function explodeTrackFeatures(trackFeatures, xml, filename) {
  const base = filename.replace(/\.gpx$/i, '');
  const metaPerTrk = readQuerenciaTrackMeta(xml);
  const groups = [];
  trackFeatures.forEach((f, fi) => {
    const lines = f.geometry.type === 'LineString' ? [f.geometry.coordinates] : f.geometry.coordinates;
    const metas = metaPerTrk?.[fi];
    if (metas && metas.length) {
      let cursor = 0;
      for (const m of metas) {
        const chunk = lines.slice(cursor, cursor + m.segs);
        cursor += m.segs;
        if (!chunk.length) break;
        groups.push({ name: m.name, owner: m.owner, lines: chunk });
      }
      // Tramos sobrantes sin entrada (archivo editado a mano): una ruta más.
      if (cursor < lines.length) groups.push({ name: null, owner: null, lines: lines.slice(cursor) });
    } else {
      groups.push({ name: f.properties?.name, owner: null, lines });
    }
  });
  return groups.map((g) => ({
    name: g.name || base,
    ownerLabel: g.owner || null,
    feature: {
      type: 'Feature',
      properties: {},
      geometry: g.lines.length === 1
        ? { type: 'LineString', coordinates: g.lines[0] }
        : { type: 'MultiLineString', coordinates: g.lines },
    },
  }));
}

async function handleFiles(fileList) {
  const files = [...fileList].filter((f) => /\.gpx$/i.test(f.name));
  if (!files.length) return;

  let skipped = 0;
  let errors = 0;
  const pending = [];

  for (const file of files) {
    try {
      const text = await file.text();
      const hash = await hashString(text);
      // Los archivos multi-ruta guardan hashes con sufijo -i: comprobar ambos.
      if ((await trackHashExists(hash)) || (await trackHashExists(`${hash}-0`))) { skipped++; continue; }

      const xml = new DOMParser().parseFromString(text, 'application/xml');
      const geojson = gpxToGeoJSON(xml);
      const trackFeatures = geojson.features.filter(
        (f) => f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString',
      );
      if (!trackFeatures.length) { errors++; continue; }

      const items = explodeTrackFeatures(trackFeatures, xml, file.name);
      for (const [i, item] of items.entries()) {
        let distanceKm = 0;
        try { distanceKm = turfLength(item.feature, { units: 'kilometers' }); } catch {}
        pending.push({
          hash: items.length > 1 ? `${hash}-${i}` : hash,
          name: item.name,
          source: file.name,
          ownerLabel: item.ownerLabel,
          feature: item.feature,
          distanceKm,
        });
      }
    } catch (err) {
      errors++;
      console.error(`Error parseando ${file.name}:`, err);
    }
  }

  let added = 0;
  if (pending.length) {
    // El modal solo pregunta por las rutas SIN propietario embebido.
    const needOwner = pending.filter((p) => !p.ownerLabel);
    if (needOwner.length) {
      const ownerLabel = await askOwnerLabel(needOwner.length);
      if (ownerLabel === null) return; // cancelado por la usuaria
      for (const p of needOwner) p.ownerLabel = ownerLabel;
    }
    const addedAt = Date.now();
    for (const p of pending) {
      const row = { ...p, addedAt };
      const id = await saveTrack(row);
      state.tracks.push({ ...row, id });
      added++;
    }
    renderTracks();
    renderTrackList();
    renderOwners();
    renderStats();
    fitToTracks();
    track('rutas_cargadas');
  }
  showDropStatus({ added, skipped, errors });
}

// Línea de estado bajo el dropzone: resultado de la última importación.
const dropStatus = document.getElementById('drop-status');
let dropStatusTimer = null;

function showDropStatus({ added, skipped, errors }) {
  if (!dropStatus) return;
  const parts = [];
  if (added) parts.push(t('drop.status.added', { n: added }));
  if (skipped) parts.push(t('drop.status.skipped', { n: skipped }));
  if (errors) parts.push(t('drop.status.errors', { n: errors }));
  if (!parts.length) return;
  dropStatus.textContent = parts.join(' · ');
  dropStatus.hidden = false;
  clearTimeout(dropStatusTimer);
  dropStatusTimer = setTimeout(() => { dropStatus.hidden = true; }, 8000);
}

// -----------------------------------------------------------------------------
// Render tracks
// -----------------------------------------------------------------------------

function renderTracks() {
  const src = map.getSource('tracks');
  if (!src) return;
  const features = state.tracks
    .filter(isVisible)
    .map((t) => ({
      ...t.feature,
      properties: {
        ...(t.feature.properties || {}),
        inZone: isInZone(t),
        ownerColor: ownerColor(t.ownerLabel) || '',
        trackId: t.id,
        trackName: t.name,
        trackKm: t.distanceKm,
        trackOwner: t.ownerLabel || IMPORTED_LABEL,
      },
    }));
  src.setData({ type: 'FeatureCollection', features });
}

function isVisible(track) {
  const label = track.ownerLabel || IMPORTED_LABEL;
  if (state.hiddenOwners.has(label)) return false;
  if (state.onlyInZone && !isInZone(track)) return false;
  return true;
}

function fitToTracks(features) {
  features = features || state.tracks.map((t) => t.feature);
  if (!features.length) return;
  const bbox = bboxOfFeatures(features);
  map.fitBounds(bbox, { padding: 60, duration: 800 });
}

function bboxOfFeatures(features) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const f of features) {
    const coords = collectCoords(f.geometry);
    for (const [x, y] of coords) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return [[minX, minY], [maxX, maxY]];
}

function collectCoords(geom) {
  if (!geom) return [];
  if (geom.type === 'Point') return [geom.coordinates];
  if (geom.type === 'LineString' || geom.type === 'MultiPoint') return geom.coordinates;
  if (geom.type === 'Polygon' || geom.type === 'MultiLineString') return geom.coordinates.flat();
  if (geom.type === 'MultiPolygon') return geom.coordinates.flat(2);
  return [];
}

// -----------------------------------------------------------------------------
// Track list + stats
// -----------------------------------------------------------------------------

const tracksList = document.getElementById('tracks-list');
const tracksCount = document.getElementById('tracks-count');
const btnClearAll = document.getElementById('btn-clear-all');
const btnExportAll = document.getElementById('btn-export-all');
const btnExportZone = document.getElementById('btn-export-zone');
const statTotal = document.getElementById('stat-total');
const statInZone = document.getElementById('stat-in-zone');
const statKmTotal = document.getElementById('stat-km-total');
const statKmZone = document.getElementById('stat-km-zone');

function updateExportButtons() {
  const visibleCount = state.tracks.filter((t) => !state.hiddenOwners.has(t.ownerLabel || IMPORTED_LABEL)).length;
  const inZoneCount = state.zone
    ? state.tracks.filter((t) => !state.hiddenOwners.has(t.ownerLabel || IMPORTED_LABEL) && isInZone(t)).length
    : 0;
  if (btnExportAll) {
    btnExportAll.disabled = visibleCount === 0;
    btnExportAll.textContent = t('export.all', { n: visibleCount });
  }
  if (btnExportZone) {
    btnExportZone.disabled = !state.zone || inZoneCount === 0;
    btnExportZone.textContent = state.zone ? t('export.zone', { n: inZoneCount }) : t('export.zone.empty');
  }
}

function renderTrackList() {
  tracksList.innerHTML = '';
  for (const t of state.tracks) {
    const li = document.createElement('li');
    li.dataset.id = t.id;
    if (isInZone(t)) li.classList.add('in-zone');
    if (t.id === state.selectedTrackId) li.classList.add('selected');
    const color = ownerColor(t.ownerLabel) || '#ff3d9a';
    li.innerHTML = `
      <span class="track-owner-dot" style="background:${color};color:${color}" title="${escapeHtml(ownerDisplay(t.ownerLabel || IMPORTED_LABEL))}"></span>
      <span class="track-name" title="${escapeHtml(t.name)} · ${escapeHtml(ownerDisplay(t.ownerLabel || IMPORTED_LABEL))} · ${escapeHtml(t.source || '')}">${escapeHtml(t.name)}</span>
      <span class="track-distance">${t.distanceKm.toFixed(1)} km</span>
      <button class="track-remove" title="Eliminar">×</button>
    `;
    li.querySelector('.track-remove').addEventListener('click', async (e) => {
      e.stopPropagation();
      await deleteTrack(t.id);
    });
    li.addEventListener('click', () => selectTrack(t, { fit: true }));
    tracksList.appendChild(li);
  }
  tracksCount.textContent = state.tracks.length;
  btnClearAll.disabled = state.tracks.length === 0;
  updateExportButtons();
}

function renderStats() {
  const total = state.tracks.length;
  const inZoneTracks = state.zone ? state.tracks.filter(isInZone) : [];
  const inZone = inZoneTracks.length;
  const kmTotal = state.tracks.reduce((acc, t) => acc + t.distanceKm, 0);
  const kmZone = inZoneTracks.reduce((acc, t) => acc + t.distanceKm, 0);
  statTotal.textContent = total;
  statInZone.textContent = state.zone ? inZone : '—';
  statKmTotal.textContent = kmTotal.toFixed(0);
  statKmZone.textContent = state.zone ? kmZone.toFixed(0) : '—';
  updateExportButtons();
}

async function deleteTrack(id) {
  if (state.selectedTrackId === id) clearTrackSelection();
  await removeTrack(id);
  state.tracks = state.tracks.filter((t) => t.id !== id);
  renderTracks();
  renderTrackList();
  renderOwners();
  renderStats();
}

function focusTrack(track) {
  const bbox = bboxOfFeatures([track.feature]);
  map.fitBounds(bbox, { padding: 100, duration: 600 });
}

// -----------------------------------------------------------------------------
// Selección de ruta (mapa ↔ lista)
// -----------------------------------------------------------------------------

let trackPopup = null;

function centerOfTrack(track) {
  const [[minX, minY], [maxX, maxY]] = bboxOfFeatures([track.feature]);
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

function selectTrack(tr, { lngLat = null, fit = false } = {}) {
  state.selectedTrackId = tr.id;
  if (map.getLayer('tracks-highlight')) {
    map.setFilter('tracks-highlight', ['==', ['get', 'trackId'], tr.id]);
  }
  tracksList.querySelectorAll('li').forEach((li) => {
    li.classList.toggle('selected', Number(li.dataset.id) === tr.id);
  });
  tracksList.querySelector('li.selected')?.scrollIntoView({ block: 'nearest' });
  if (fit) focusTrack(tr);

  trackPopup?.remove();
  trackPopup = new maplibregl.Popup({ closeButton: true, className: 'track-popup', maxWidth: '260px' })
    .setLngLat(lngLat || centerOfTrack(tr))
    .setHTML(
      `<strong>${escapeHtml(tr.name)}</strong><br>` +
      `${tr.distanceKm.toFixed(1)} km · ${escapeHtml(ownerDisplay(tr.ownerLabel || IMPORTED_LABEL))}`,
    )
    .addTo(map);
  trackPopup.on('close', () => {
    // Solo des-selecciona si el popup cerrado es el de la ruta activa
    // (al cambiar de ruta, el remove() del popup anterior también dispara 'close').
    if (state.selectedTrackId === tr.id) clearTrackSelection({ closePopup: false });
  });
}

function clearTrackSelection({ closePopup = true } = {}) {
  state.selectedTrackId = null;
  if (map.getLayer('tracks-highlight')) {
    map.setFilter('tracks-highlight', ['==', ['get', 'trackId'], -1]);
  }
  tracksList.querySelectorAll('li.selected').forEach((li) => li.classList.remove('selected'));
  if (closePopup) { trackPopup?.remove(); trackPopup = null; }
}

btnClearAll.addEventListener('click', async () => {
  if (!state.tracks.length) return;
  if (!confirm(t('tracks.clearAll.confirm', { n: state.tracks.length }))) return;
  await clearAllTracks();
  clearTrackSelection();
  state.tracks = [];
  state.hiddenOwners.clear();
  renderTracks();
  renderTrackList();
  renderOwners();
  renderStats();
});

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// -----------------------------------------------------------------------------
// Zone tabs
// -----------------------------------------------------------------------------

const zoneTabs = [...document.querySelectorAll('.zone-tab')];
const zonePanes = [...document.querySelectorAll('.zone-pane')];

for (const tab of zoneTabs) {
  tab.addEventListener('click', () => {
    const mode = tab.dataset.mode;
    state.zoneMode = mode;
    zoneTabs.forEach((t) => t.classList.toggle('active', t === tab));
    zonePanes.forEach((p) => p.classList.toggle('hidden', p.dataset.pane !== mode));
    if (mode !== 'draw' && state.drawing) cancelDrawing();
  });
}

// -----------------------------------------------------------------------------
// Search mode (Nominatim)
// -----------------------------------------------------------------------------

const searchInput = document.getElementById('search-input');
const searchResults = document.getElementById('search-results');

let searchAbort = null;
let searchTimer = null;

searchInput.addEventListener('input', () => {
  clearTimeout(searchTimer);
  const q = searchInput.value.trim();
  if (q.length < 2) { hideResults(searchResults); return; }
  searchTimer = setTimeout(() => runSearch(q, searchResults, applySearchResult), 350);
});

document.addEventListener('click', (e) => {
  if (!searchInput.contains(e.target) && !searchResults.contains(e.target)) hideResults(searchResults);
  const radiusInput = document.getElementById('radius-input');
  if (!radiusInput.contains(e.target) && !radiusResults.contains(e.target)) hideResults(radiusResults);
});
searchInput.addEventListener('focus', () => {
  if (searchResults.children.length) searchResults.classList.add('visible');
});

async function runSearch(query, listEl, onPick) {
  if (searchAbort) searchAbort.abort();
  searchAbort = new AbortController();
  listEl.innerHTML = `<li class="no-results">${escapeHtml(t('search.searching'))}</li>`;
  listEl.classList.add('visible');
  try {
    const results = await searchPlace(query, { signal: searchAbort.signal });
    listEl.innerHTML = '';
    if (!results.length) {
      listEl.innerHTML = `<li class="no-results">${escapeHtml(t('search.noResults'))}</li>`;
      return;
    }
    for (const r of results) {
      const li = document.createElement('li');
      li.innerHTML = `
        <span class="res-name">${escapeHtml(r.shortName)}</span>
        <span class="res-detail">${escapeHtml(r.name)}</span>
      `;
      li.addEventListener('click', () => onPick(r, listEl));
      listEl.appendChild(li);
    }
  } catch (err) {
    if (err.name === 'AbortError') return;
    console.error('Nominatim error:', err);
    listEl.innerHTML = `<li class="no-results">${escapeHtml(t('search.error'))}</li>`;
  }
}

function hideResults(listEl) {
  listEl.classList.remove('visible');
}

function applySearchResult(result) {
  const feature = featureFromNominatim(result);
  if (!feature) {
    alert(t('zone.noPolygon'));
    return;
  }
  setZone(feature);
  hideResults(searchResults);
  searchInput.value = result.shortName;
  const bbox = bboxOfFeatures([feature]);
  map.fitBounds(bbox, { padding: 80, duration: 800 });
}

function featureFromNominatim(r) {
  if (r.geometry && (r.geometry.type === 'Polygon' || r.geometry.type === 'MultiPolygon')) {
    return { type: 'Feature', geometry: r.geometry, properties: { source: 'nominatim', name: r.shortName } };
  }
  if (r.bbox) {
    const [w, s, e, n] = r.bbox;
    return turfPolygon([[[w, s], [e, s], [e, n], [w, n], [w, s]]], { source: 'nominatim-bbox', name: r.shortName });
  }
  return null;
}

// -----------------------------------------------------------------------------
// Radius mode
// -----------------------------------------------------------------------------

const radiusInput = document.getElementById('radius-input');
const radiusResults = document.getElementById('radius-results');
const radiusSlider = document.getElementById('radius-slider');
const radiusValue = document.getElementById('radius-value');
const radiusCenterHint = document.getElementById('radius-center-hint');

let radiusSearchTimer = null;

radiusInput.addEventListener('input', () => {
  clearTimeout(radiusSearchTimer);
  const q = radiusInput.value.trim();
  if (q.length < 2) { hideResults(radiusResults); return; }
  radiusSearchTimer = setTimeout(() => runSearch(q, radiusResults, applyRadiusCenter), 350);
});
radiusInput.addEventListener('focus', () => {
  if (radiusResults.children.length) radiusResults.classList.add('visible');
});

radiusSlider.addEventListener('input', () => {
  state.radiusKm = Number(radiusSlider.value);
  radiusValue.textContent = `${state.radiusKm} km`;
  if (state.radiusCenter) applyRadiusZone();
});

function applyRadiusCenter(result, listEl) {
  state.radiusCenter = { lat: result.lat, lon: result.lon, label: result.shortName };
  radiusInput.value = result.shortName;
  radiusCenterHint.textContent = t('zone.radius.center', { place: result.shortName });
  hideResults(listEl);
  applyRadiusZone();
}

function applyRadiusZone() {
  if (!state.radiusCenter) return;
  const { lat, lon } = state.radiusCenter;
  const circle = turfCircle([lon, lat], state.radiusKm, { units: 'kilometers', steps: 96 });
  setZone(circle);
  const bbox = bboxOfFeatures([circle]);
  map.fitBounds(bbox, { padding: 80, duration: 800 });
}

// -----------------------------------------------------------------------------
// Draw mode
// -----------------------------------------------------------------------------

const btnDraw = document.getElementById('btn-draw');
const btnClearZone = document.getElementById('btn-clear-zone');
const onlyInZoneChk = document.getElementById('only-in-zone');
const mapEl = document.getElementById('map');

btnDraw.addEventListener('click', startDrawing);
btnClearZone.addEventListener('click', clearZone);
onlyInZoneChk.addEventListener('change', () => {
  state.onlyInZone = onlyInZoneChk.checked;
  renderTracks();
  renderTrackList();
  renderStats();
});

function startDrawing() {
  if (state.drawing) return;
  state.drawing = true;
  state.drawPoints = [];
  mapEl.classList.add('drawing');
  btnDraw.textContent = t('zone.draw.drawing');
  btnDraw.disabled = true;
  updateDrawPreview();
}

function cancelDrawing() {
  state.drawing = false;
  state.drawPoints = [];
  mapEl.classList.remove('drawing');
  btnDraw.textContent = t('zone.draw.start');
  btnDraw.disabled = false;
  updateDrawPreview();
}

function finishDrawing() {
  if (state.drawPoints.length < 3) { cancelDrawing(); return; }
  const ring = [...state.drawPoints, state.drawPoints[0]];
  setZone(turfPolygon([ring], { source: 'draw' }));
  cancelDrawing();
}

function updateDrawPreview() {
  const src = map.getSource('draw-preview');
  if (!src) return;
  if (!state.drawing || !state.drawPoints.length) {
    src.setData(emptyFC());
    return;
  }
  const features = state.drawPoints.map((p) => ({
    type: 'Feature', geometry: { type: 'Point', coordinates: p }, properties: {},
  }));
  if (state.drawPoints.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: state.drawPoints },
      properties: {},
    });
  }
  src.setData({ type: 'FeatureCollection', features });
}

map.on('click', (e) => {
  if (!state.drawing) return;
  state.drawPoints.push([e.lngLat.lng, e.lngLat.lat]);
  updateDrawPreview();
});
map.on('dblclick', (e) => {
  if (!state.drawing) return;
  e.preventDefault();
  finishDrawing();
});
window.addEventListener('keydown', (e) => {
  if (!state.drawing) return;
  if (e.key === 'Escape') cancelDrawing();
  if (e.key === 'Enter') finishDrawing();
});

// -----------------------------------------------------------------------------
// Zone management (shared)
// -----------------------------------------------------------------------------

function setZone(feature) {
  state.zone = feature;
  map.getSource('zone').setData({ type: 'FeatureCollection', features: [feature] });
  btnClearZone.disabled = false;
  renderTracks();
  renderTrackList();
  renderStats();
}

function clearZone() {
  state.zone = null;
  state.radiusCenter = null;
  radiusCenterHint.textContent = t('zone.radius.noCenter');
  radiusInput.value = '';
  map.getSource('zone').setData(emptyFC());
  btnClearZone.disabled = true;
  renderTracks();
  renderTrackList();
  renderStats();
}

function isInZone(track) {
  if (!state.zone) return false;
  try {
    return booleanIntersects(track.feature, state.zone);
  } catch {
    return false;
  }
}

// -----------------------------------------------------------------------------
// Basemap management
// -----------------------------------------------------------------------------

const BASEMAP_LAYER_IDS = ['basemap-raster', 'basemap-overlay'];
const BASEMAP_SOURCE_IDS = ['basemap', 'basemap-overlay'];
const APP_BG_LAYER = 'app-background';

function setBasemap(id, { fit = false, persist = true } = {}) {
  const config = BASEMAPS[id] || BASEMAPS[DEFAULT_BASEMAP];
  state.basemapId = id;
  // Solo persistimos la elección EXPLÍCITA del usuario, no el default de carga:
  // así el default no queda "pegado" y futuros cambios de default sí se propagan.
  if (persist) { try { localStorage.setItem(BASEMAP_STORAGE_KEY, id); } catch {} }

  // Limpiar layers/sources de basemap previos
  for (const layerId of BASEMAP_LAYER_IDS) {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  }
  for (const srcId of BASEMAP_SOURCE_IDS) {
    if (map.getSource(srcId)) map.removeSource(srcId);
  }

  // Fondo de la app (solo cambia en modos "sin tiles")
  if (config.background) {
    map.setPaintProperty(APP_BG_LAYER, 'background-color', config.background);
  } else {
    map.setPaintProperty(APP_BG_LAYER, 'background-color', '#0a0c10');
  }

  // Añadir raster principal si existe
  if (config.tiles) {
    map.addSource('basemap', {
      type: 'raster',
      tiles: config.tiles,
      tileSize: 256,
      maxzoom: config.maxzoom || 19,
      attribution: config.attribution,
    });
    map.addLayer({ id: 'basemap-raster', type: 'raster', source: 'basemap' }, 'tracks-outline');
  }

  // Overlay opcional (etiquetas suaves, etc.)
  if (config.overlay?.tiles) {
    map.addSource('basemap-overlay', {
      type: 'raster',
      tiles: config.overlay.tiles,
      tileSize: 256,
      maxzoom: config.overlay.maxzoom || 19,
      attribution: config.attribution,
    });
    map.addLayer({
      id: 'basemap-overlay',
      type: 'raster',
      source: 'basemap-overlay',
      paint: { 'raster-opacity': config.overlay.opacity ?? 0.3 },
    }, 'tracks-outline');
  }

  applyTrackTheme(config.trackTheme);

  const desc = document.getElementById('basemap-description');
  if (desc) desc.textContent = t(`basemap.${id}.desc`);

  // Refrescar chips activos
  document.querySelectorAll('.basemap-chip').forEach((el) => {
    el.classList.toggle('active', el.dataset.id === id);
  });

  if (fit && state.tracks.length) fitToTracks();
}

function applyTrackTheme(theme) {
  if (!theme || !map.getLayer('tracks-line')) return;
  // Color: si la ruta está en zona → zoneColor del tema. Si tiene ownerColor → ese. Si no, color del tema.
  map.setPaintProperty('tracks-line', 'line-color', [
    'case',
    ['boolean', ['get', 'inZone'], false], theme.zoneColor,
    ['all', ['has', 'ownerColor'], ['!=', ['get', 'ownerColor'], '']], ['get', 'ownerColor'],
    theme.color,
  ]);
  map.setPaintProperty('tracks-line', 'line-width', theme.width ?? 3);
  map.setPaintProperty('tracks-line', 'line-opacity', theme.opacity ?? 0.75);
  map.setPaintProperty('tracks-line', 'line-blur', theme.blur ?? 0);
  map.setPaintProperty('tracks-outline', 'line-color', theme.outline ?? '#000');
  map.setPaintProperty('tracks-outline', 'line-width', theme.outlineWidth ?? 5);
  map.setPaintProperty('tracks-outline', 'line-opacity', theme.outlineOpacity ?? 0.35);
  // La capa de selección hereda el color del tema, más ancha y opaca.
  if (map.getLayer('tracks-highlight')) {
    map.setPaintProperty('tracks-highlight', 'line-color', [
      'case',
      ['boolean', ['get', 'inZone'], false], theme.zoneColor,
      ['all', ['has', 'ownerColor'], ['!=', ['get', 'ownerColor'], '']], ['get', 'ownerColor'],
      theme.color,
    ]);
    map.setPaintProperty('tracks-highlight', 'line-width', (theme.width ?? 3) + 3.5);
  }
}

function renderBasemapChips() {
  const container = document.getElementById('basemap-chips');
  if (!container) return;
  container.innerHTML = '';
  for (const id of BASEMAP_ORDER) {
    const config = BASEMAPS[id];
    if (!config) continue;
    const chip = document.createElement('button');
    chip.className = 'basemap-chip';
    chip.dataset.id = id;
    chip.setAttribute('role', 'radio');
    chip.title = t(`basemap.${id}.desc`);
    chip.innerHTML = `<span class="chip-dot"></span><span>${escapeHtml(t(`basemap.${id}.label`))}</span>`;
    if (id === state.basemapId) chip.classList.add('active');
    chip.addEventListener('click', () => { setBasemap(id); track('cambia_mapa'); });
    container.appendChild(chip);
  }
  const desc = document.getElementById('basemap-description');
  if (desc) desc.textContent = t(`basemap.${state.basemapId}.desc`);
}

// -----------------------------------------------------------------------------
// Owners (modal + chips)
// -----------------------------------------------------------------------------

const ownerModal = document.getElementById('owner-modal');
const ownerInput = document.getElementById('owner-input');
const ownerModalTitle = document.getElementById('owner-modal-title');
const ownerConfirm = document.getElementById('owner-confirm');
const ownerCancel = document.getElementById('owner-cancel');
const ownerDatalist = document.getElementById('owners-datalist');
const ownersList = document.getElementById('owners-list');

let ownerResolver = null;

function askOwnerLabel(fileCount) {
  return new Promise((resolve) => {
    ownerResolver = resolve;
    ownerModalTitle.textContent = fileCount === 1 ? t('modal.title.one') : t('modal.title.many', { n: fileCount });
    ownerInput.value = '';
    // Populate datalist with existing owners
    ownerDatalist.innerHTML = '';
    const existing = uniqueOwners(state.tracks).map((o) => o.label);
    for (const label of existing) {
      if (label === IMPORTED_LABEL) continue;
      const opt = document.createElement('option');
      opt.value = label;
      ownerDatalist.appendChild(opt);
    }
    ownerModal.classList.remove('hidden');
    setTimeout(() => ownerInput.focus(), 30);
  });
}

function closeOwnerModal(value) {
  ownerModal.classList.add('hidden');
  const r = ownerResolver;
  ownerResolver = null;
  if (r) r(value);
}

ownerConfirm.addEventListener('click', () => {
  const v = ownerInput.value.trim();
  closeOwnerModal(v || IMPORTED_LABEL);
});
ownerCancel.addEventListener('click', () => closeOwnerModal(null));
ownerInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') { e.preventDefault(); ownerConfirm.click(); }
  if (e.key === 'Escape') { e.preventDefault(); ownerCancel.click(); }
});
ownerModal.addEventListener('click', (e) => {
  if (e.target === ownerModal) closeOwnerModal(null);
});

function renderOwners() {
  if (!ownersList) return;
  const owners = uniqueOwners(state.tracks);
  ownersList.innerHTML = '';
  for (const o of owners) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'owner-chip';
    if (state.hiddenOwners.has(o.label)) chip.classList.add('hidden');
    const color = o.color || '#ff3d9a';
    chip.innerHTML = `
      <span class="owner-dot" style="background:${color};color:${color}"></span>
      <span>${escapeHtml(ownerDisplay(o.label))}</span>
      <span class="owner-count">${o.count}</span>
    `;
    chip.title = state.hiddenOwners.has(o.label) ? t('owners.show') : t('owners.hide');
    chip.addEventListener('click', () => toggleOwner(o.label));
    ownersList.appendChild(chip);
  }
}

function toggleOwner(label) {
  if (state.hiddenOwners.has(label)) state.hiddenOwners.delete(label);
  else state.hiddenOwners.add(label);
  renderTracks();
  renderOwners();
  renderStats();
}

// -----------------------------------------------------------------------------
// GPX export
// -----------------------------------------------------------------------------

btnExportAll.addEventListener('click', () => {
  const visible = state.tracks.filter((t) => !state.hiddenOwners.has(t.ownerLabel || IMPORTED_LABEL));
  exportTracks(visible, 'wikitot-todas');
});
btnExportZone.addEventListener('click', () => {
  if (!state.zone) return;
  const sel = state.tracks.filter((t) => !state.hiddenOwners.has(t.ownerLabel || IMPORTED_LABEL) && isInZone(t));
  exportTracks(sel, 'wikitot-zona');
});

function exportTracks(tracks, baseName) {
  if (!tracks.length) {
    alert(t('export.noTracks'));
    return;
  }
  const name = composeExportName(tracks);
  const gpx = tracksToGPX(tracks, {
    name,
    mode: 'single',
    includeTime: false,
    manifestHeader: t('export.manifestHeader', { n: tracks.length }),
  });
  const stamp = new Date().toISOString().slice(0, 10);
  downloadGPX(`${safeFilename(baseName)}-${stamp}.gpx`, gpx);
  track('descarga_ruta');
}

// Nombre del track combinado: si todas las rutas son del mismo propietario
// (no "Importado"), lo usa; si no, genérico. Ej: "Cristóbal · 12 rutas".
function composeExportName(tracks) {
  const owners = uniqueOwners(tracks);
  const named = owners.filter((o) => o.label !== IMPORTED_LABEL);
  if (named.length === 1 && owners.length === 1) {
    return t('export.name.owner', { owner: named[0].label, n: tracks.length });
  }
  return t('export.name.generic', { n: tracks.length });
}

// -----------------------------------------------------------------------------
// i18n wiring
// -----------------------------------------------------------------------------

const langSwitch = document.getElementById('lang-switch');

function refreshLangSwitch() {
  langSwitch?.querySelectorAll('button').forEach((b) => {
    b.classList.toggle('active', b.dataset.lang === getLang());
  });
}

// Título del documento y meta description según idioma.
function applyDocumentMeta() {
  document.title = t('app.title');
  document.querySelector('meta[name="description"]')?.setAttribute('content', t('app.metaDesc'));
  document.getElementById('fit-tracks-btn')?.setAttribute('title', t('map.fit'));
}

function onLangChange(lang) {
  if (lang === getLang()) return;
  setLang(lang);
  track('idioma_cambiado');
  applyStaticTranslations();
  applyDocumentMeta();
  refreshLangSwitch();
  // Re-render de las partes generadas dinámicamente
  renderBasemapChips();
  const desc = document.getElementById('basemap-description');
  if (desc) desc.textContent = t(`basemap.${state.basemapId}.desc`);
  renderOwners();
  renderTrackList();
  renderStats();
  updateExportButtons();
  // Estados dependientes del idioma
  radiusCenterHint.textContent = state.radiusCenter
    ? t('zone.radius.center', { place: state.radiusCenter.label })
    : t('zone.radius.noCenter');
  btnDraw.textContent = state.drawing ? t('zone.draw.drawing') : t('zone.draw.start');
}

langSwitch?.querySelectorAll('button').forEach((b) => {
  b.addEventListener('click', () => onLangChange(b.dataset.lang));
});

// -----------------------------------------------------------------------------
// Tema claro/oscuro (Fanzine)
// -----------------------------------------------------------------------------

const themeToggle = document.getElementById('theme-toggle');

function refreshThemeToggle() {
  const mode = getMode();
  themeToggle?.querySelectorAll('button').forEach((b) => {
    const active = b.dataset.mode === mode;
    b.classList.toggle('active', active);
    b.setAttribute('aria-pressed', String(active));
  });
}

themeToggle?.querySelectorAll('button').forEach((b) => {
  b.addEventListener('click', () => { setMode(b.dataset.mode); refreshThemeToggle(); track('tema_cambiado'); });
});

// Café (Ko-fi) — evento anónimo de interés/apoyo
document.getElementById('coffee-link')?.addEventListener('click', () => track('cafe_click'));

initMode();
refreshThemeToggle();
onSystemChange(refreshThemeToggle);

// Init i18n (aplica idioma detectado/persistido al cargar)
setLang(getLang());
applyStaticTranslations();
applyDocumentMeta();
refreshLangSwitch();

// Expose for debug
if (typeof window !== 'undefined') {
  window.__wikitot = { state, db, map, setBasemap, tracksToGPX, composeExportName, setLang, getLang, handleFiles };
}
